import axios from 'axios';
import { API_TOKEN } from '../constantes/constantes';

const AGENT_CORE_URL = 'https://bedrock-agentcore.us-east-1.amazonaws.com';
const PORTAL = 'utv';

/**
 * Obtiene un nuevo token de acceso desde el endpoint de AgentCore
 *
 * @async
 * @returns {Promise<{accessToken: string, runtimeArn: string}>} Token y ARN del Runtime
 * @throws {Error} Si falla la obtención del token
 */
export async function getValidToken() {
  try {
    const response = await axios.get(API_TOKEN, {
      timeout: 10000
    });

    if (!response.data.access_token || !response.data.runtime_arn) {
      throw new Error('No access_token o runtime_arn en respuesta');
    }

    return {
      accessToken: response.data.access_token,
      runtimeArn: response.data.runtime_arn,
    };
  } catch (error) {
    console.error('Error obteniendo token', error.message);
    throw error;
  }
}

/**
 * Servicio de búsqueda agéntica (amx_finder_agent en AWS Bedrock AgentCore)
 *
 * @description Una sola invocación por turno: el agente responde en streaming (SSE) y, al
 * terminar, envía un evento `{"type": "fuentes", "notas": [...], "partidos": []}` con las
 * notas para las tarjetas (UnoTV no tiene partidos).
 * El agente no guarda memoria: el contexto de la conversación viaja en `history`.
 *
 * @async
 * @function getSearch
 *
 * @param {string} query - Pregunta del usuario en lenguaje natural
 * @param {Object} options
 * @param {Object} options.token - Resultado de getValidToken()
 * @param {string} options.sessionId - ID de sesión (UUID); el mismo durante toda la conversación
 * @param {Array<{role: string, content: string}>} [options.history] - Turnos anteriores
 * @param {string} [options.origen] - Cómo llegó la consulta: 'escrita', 'sugerencia' o 'url' (tracking)
 * @param {Function} options.onChunk - Callback por cada fragmento de texto
 * @param {Function} [options.onSources] - Callback con el evento de fuentes (notas)
 * @param {AbortSignal} [options.signal] - Señal para cancelar la petición
 *
 * @returns {Promise<{data: Object|Array, statusCode: number, aborted?: boolean}>}
 */
export async function getSearch(query, { token, sessionId, history = [], origen = null, onChunk, onSources, signal } = {}) {
  if (!query || typeof query !== 'string') {
    return { statusCode: 400, data: [] };
  }

  const url = `${AGENT_CORE_URL}/runtimes/${encodeURIComponent(token.runtimeArn)}/invocations?qualifier=DEFAULT`;
  const requestData = {
    prompt: query.trim(),
    portal: PORTAL,
    session_id: sessionId,
    history,
    ...(origen ? { origen } : {}),
  };

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token.accessToken}`,
        'Accept': 'text/event-stream',
        'Content-Type': 'application/json; charset=utf-8',
        // Reusar la sesión evita el arranque en frío de AgentCore en cada turno.
        'X-Amzn-Bedrock-AgentCore-Runtime-Session-Id': sessionId,
      },
      body: JSON.stringify(requestData),
      signal,
    });

    if (!response.ok) {
      console.error('[getSearch] Error HTTP:', response.status);
      return { statusCode: response.status, data: [] };
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let fullContent = '';

    const handleLine = (line) => {
      const event = parseSSELine(line);
      if (typeof event === 'string') {
        onChunk?.(event);
        fullContent += event;
      } else if (event?.type === 'fuentes') {
        onSources?.(event);
      }
    };

    while (true) {
      const { done, value } = await reader.read();

      if (done) {
        if (buffer.trim()) handleLine(buffer.trim());
        break;
      }

      buffer += decoder.decode(value, { stream: true });

      // Procesar líneas completas (SSE usa \n como separador)
      const lines = buffer.split('\n');
      buffer = lines[lines.length - 1]; // Guardar línea incompleta

      for (let i = 0; i < lines.length - 1; i++) {
        const line = lines[i].trim();
        if (line) handleLine(line);
      }
    }

    return {
      data: { resume: fullContent },
      statusCode: 200
    };

  } catch (error) {
    if (error?.name === 'AbortError') {
      return { statusCode: 499, data: [], aborted: true };
    }
    console.error('[getSearch] Error:', error.message);
    return { statusCode: 500, data: [] };
  }
}

/**
 * Parsea una línea en formato Server-Sent Events (SSE)
 * Formato: `data: "texto"` (fragmento de respuesta) o `data: {...}` (evento estructurado)
 *
 * @private
 * @param {string} line - Línea a procesar
 * @returns {string|Object|null} Texto, objeto del evento o null si no es válido
 */
export function parseSSELine(line) {
  const content = line.startsWith('data:') ? line.substring(5).trim() : line;
  try {
    const parsed = JSON.parse(content);
    if (typeof parsed === 'string') return parsed.length > 0 ? parsed : null;
    if (parsed && typeof parsed === 'object') return parsed;
    return null;
  } catch (e) {
    console.warn('[parseSSELine] Error parseando línea:', e.message, 'Línea:', line.substring(0, 100));
    return null;
  }
}
